import React, { useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';

import { getAddressDetails } from '../api-config/index';
import { WidgetProps } from '../interfaces/Types';
import { AppHelper } from '../utils/AppHelper';
import {
  ageResolver,
  isValidSSNOrCoordinationNumber,
  isVolontaryPhoneValid
} from '../utils/CalculationHelper';
interface InputWidgetProps {
  enonicData: WidgetProps;
}

export const InputWidgetContainer: React.FC<InputWidgetProps> = ({
  enonicData
}: InputWidgetProps) => {
  const [personNumber, setPersonNumber] = useState('');
  const [validPersonNumber, isValidPersonNumber] = useState(false);
  const [personNumberErrorText, setPersonNumberErrorText] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [validZipCode, isValidZipCode] = useState(false);
  const [zipCodeErrorText, setZipCodeErrorText] = useState('');
  const [address, setAddress] = useState('');
  const [validAddress, setValidAddress] = useState(false);
  const [addressErrorText, setAddressErrorText] = useState('');
  const [addressLoader, setAddressLoader] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validPhoneNumber, setValidPhoneNumber] = useState(true);
  const [addressFromService, setAddressFromService] = useState({
    calculationStatus: {
      error: false
    },
    customerName: '',
    adress: '',
    zipCode: '',
    cpr: ''
  });

  const MIN_YEAR_PREFIX = 19;
  const ZIP_CODE_LENGTH = 5;
  const MIN_AGE = 18;
  const NEXT_PAGE_URL_PATH = '/links/webshop-home';
  const MAX_ADDR_LENGTH = 40;
  const MIN_ADDR_LENGTH = 3;

  const handleOnChangePersonNumber = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    setPersonNumber(customerInput.toUpperCase());
    const isPersonNumberValid = cprAndAgeValidator(
      { age: MIN_AGE },
      customerInput
    );
    isValidPersonNumber(isPersonNumberValid);
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    }
    if (isPersonNumberValid) {
      setAddressLoader(true);
      getAddressFromSSN(customerInput);
    } else {
      setAddress('');
      setValidAddress(false);
    }
  };

  const handleOnBlurPersonNumber = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    } else {
      if (validPersonNumber) {
        setPersonNumberErrorText('');
      } else {
        if (!isValidSSNOrCoordinationNumber(customerInput)) {
          setPersonNumberErrorText(enonicData.cprInvalidMessage);
        } else if (ageResolver(customerInput) < MIN_AGE) {
          setPersonNumberErrorText(enonicData.ageBelow18Text);
        }
      }
    }
  };

  const handleZipCodeChange = (event: any) => {
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    setZipCode(customerInput);
    const isZipValid = zipCodeValidator(customerInput);
    isValidZipCode(isZipValid);
    if (!customerInput.length) {
      setZipCodeErrorText('');
    }
    if (isZipValid && addressFromService.zipCode === customerInput) {
      setAddress(addressFromService.adress);
      setValidAddress(true);
    }
  };

  const handleAddressChange = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    if (customerInput) {
      setAddress(event.currentTarget.value);
    } else {
      setAddress(customerInput);
    }
    const isAddressValid = validateAddress(event.currentTarget.value);
    setValidAddress(isAddressValid);
  };

  const handleOnBlurAddress = (event: any) => {
    const customerInput = event.currentTarget.value;
    const isAddressValid = validateAddress(event.currentTarget.value);
    if (!isAddressValid && customerInput.length) {
      setAddressErrorText(enonicData.addressErrorText);
    } else {
      setAddressErrorText('');
    }
  };

  const validateAddress = (address: any) =>
    address.length <= MAX_ADDR_LENGTH && address.length >= MIN_ADDR_LENGTH;

  const getAddressFromSSN = async (validSSN: number) => {
    const addressFromSSN = await getAddressDetails(validSSN);
    setAddressLoader(false);
    if (addressFromSSN && !addressFromSSN.calculationStatus.error) {
      setAddressFromService(addressFromSSN);
      if (validZipCode && addressFromSSN.zipCode === zipCode) {
        setAddress(addressFromSSN.adress);
        setValidAddress(true);
      }
    } else {
      setAddress('');
    }
  };

  const handleOnBlurZipCode = (event: any) => {
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    if (!customerInput.length) {
      setZipCodeErrorText('');
    } else {
      if (validZipCode) {
        setZipCodeErrorText('');
      } else {
        if (!zipCodeValidator(customerInput)) {
          setZipCodeErrorText(enonicData.zipCodeErrorText);
        }
      }
    }
  };

  const zipCodeValidator = (value: any) => {
    return value.length === ZIP_CODE_LENGTH;
  };

  const cprAndAgeValidator = (options: any, inputValue: any) => {
    if (
      (inputValue.length !== 10 && inputValue.length !== 12) ||
      (inputValue.length === 12 &&
        Number(inputValue.slice(0, 2)) < MIN_YEAR_PREFIX) ||
      !isValidSSNOrCoordinationNumber(inputValue)
    ) {
      return false;
    } else {
      if (ageResolver(inputValue) < options.age) {
        return false;
      } else return true;
    }
  };

  const handleOnChangePhoneNumber = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    setPhoneNumber(customerInput);

    if (!validPhoneNumber) {
      const isPhoneValid = isVolontaryPhoneValid(event.currentTarget.value);
      setValidPhoneNumber(isPhoneValid);
    }
  };

  const handleOnBlurPhoneNumber = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    setPhoneNumber(customerInput);
    const isPhoneValid = isVolontaryPhoneValid(customerInput);
    if (!isPhoneValid) {
      setValidPhoneNumber(isPhoneValid);
      sessionStorage.removeItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY);
    } else {
      setValidPhoneNumber(isPhoneValid);
      sessionStorage.setItem(
        AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY,
        phoneNumber
      );
    }
  };

  const gotoNextStep = (event: any) => {
    event.preventDefault();
    const customerInfo = {
      cprNumber: personNumber,
      customerName: addressFromService.customerName,
      zipCode: zipCode,
      customerAddress: address,
      houseType: 1,
      channel: 'rikshem'
    };
    sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));

    if (NEXT_PAGE_URL_PATH) {
      window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
    }
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  // To disable phone number in form toggle this to false.
  const showVolontaryPhoneNumber = true;

  const homeParams: CalculationWidgetProps = {
    headerText: enonicData.headerText,
    image: AppHelper.getImageCDNPath(enonicData.imageInfo.image),
    smallScreenImage: AppHelper.getImageCDNPath(
      enonicData.imageInfo.smallScreenImage
    ),
    imageAltText: enonicData.imageInfo.imageAltText,
    inputLists: {
      calculationInputs: [
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'cpr-number',
              labelText: enonicData.cprNumberLabel,
              name: 'input-cprNumber',
              onChange: handleOnChangePersonNumber,
              onBlur: handleOnBlurPersonNumber,
              value: personNumber,
              className: '',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'ssn',
              invalid: !validPersonNumber && !!personNumberErrorText,
              errorText: personNumberErrorText,
              helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
              isHelpTextAlwaysVisible: true,
              moreInfoModal: {
                linkText: 'Varför frågar vi om personnummer?',
                title: 'Personnummer',
                content: getCprMoreInfoContent(),
                closeLabel: 'Stäng hjälptext',
                ariaLabel: 'Hjälp om personnummer',
                wrapperClassName: 'cpr-modal-wrapper',
                ariaDescribedBy: 'cpr-more-info'
              },
              inputMode: 'numeric'
            }
          ]
        },
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'post-number',
              labelText: enonicData.zipCodeLabel,
              name: 'postNumber',
              onChange: handleZipCodeChange,
              onBlur: handleOnBlurZipCode,
              value: zipCode,
              placeholder: '',
              className: '',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'zip-code',
              invalid: !validZipCode && !!zipCodeErrorText,
              errorText: zipCodeErrorText,
              helpText: '',
              inputMode: 'numeric',
              postfixLoader: addressLoader && validZipCode && !zipCodeErrorText,
              autoCompleteLabel: 'postal-code'
            }
          ]
        },
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'gatuadress',
              labelText: enonicData.addressLabel,
              name: 'input-gatuadress',
              onChange: handleAddressChange,
              onBlur: handleOnBlurAddress,
              value: address,
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'street-address',
              invalid: !validAddress && !!addressErrorText,
              errorText: addressErrorText,
              autoCompleteLabel: 'street-address'
            }
          ]
        },
        {
          type: 'inputText',
          isHidden: !showVolontaryPhoneNumber,
          inputListInRow: [
            {
              id: 'phone-number',
              labelText: 'Mobil (frivilligt)',
              name: 'input-phoneNumber',
              onChange: handleOnChangePhoneNumber,
              onBlur: handleOnBlurPhoneNumber,
              value: phoneNumber,
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'phone-number',
              invalid: !validPhoneNumber,
              errorText: AppHelper.phoneNumberErrorText,
              helpText: AppHelper.phoneNrHelpText,
              helpTextToggleLabel: 'Visa hjälptext för Mobil',
              helpTextCloseLabel: 'Stäng hjälptext för Mobil',
              inputMode: 'numeric',
              autoCompleteLabel: 'tel'
            }
          ]
        }
      ],
      calculateBtn: {
        btnText: enonicData.calculateBtnLabel,
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: !validPersonNumber || !validZipCode || !validAddress,
        className: 'calculate-price mt-1'
      }
    },
    additionalInfoLinkText: enonicData.personalDataHandlingInfoLinkText,
    additionalInfoLink: enonicData.personalDataHandlinkUrl,
    footerMessage: enonicData.footerMessage
  };

  return <CalculationWidget {...homeParams} />;
};
